import { useState } from "react";
import { format, formatDistanceToNowStrict, isPast } from "date-fns";
import { CalendarClock, CheckCircle2, Loader2, Rocket, ShieldCheck, Zap } from "lucide-react";
import {
  useAdBoostPackages,
  useAdBoostStatus,
  usePurchaseAdBoost,
} from "@/hooks/use-teacher-subscription";
import type { AdBoostPackageResponse } from "@/services/teacherSubscriptionService";
import { currencySymbol } from "@/lib/currency";
import { cn } from "@/lib/utils";

const DURATION_LABEL: Record<AdBoostPackageResponse["durationDays"], string> = {
  1: "1 day",
  3: "3 days",
  7: "7 days",
};

/**
 * AdBoostPurchaseCard — self-contained ad-boost purchase flow for a teacher's
 * dashboard. A future page drops this in wherever it needs the flow; this
 * component owns all of its own data fetching and purchase handling.
 *
 * Rendering rules (see useAdBoostStatus()):
 * - An ACTIVE boost always takes priority — shows its score/dates, never the
 *   picker, even if `eligible` has since flipped false (e.g. the teacher
 *   subscribed mid-boost; the running boost still plays out).
 * - Otherwise, when `eligible` is false (a subscribed teacher isn't eligible
 *   for ad-boost), render a brief explanatory note instead of the picker. This
 *   mirrors the server's own precondition for POST /ad-boost/order, so the CTA
 *   is never shown for a purchase the server would reject with 403
 *   AD_BOOST_NOT_ELIGIBLE.
 * - Otherwise, render the 1/3/7-day package picker + purchase CTA.
 *
 * IMPORTANT (product decision, see F5 plan Open Question 11): auto-renew has
 * no backend recharge mechanism yet, so this component never renders an
 * auto-renew toggle and always purchases with `autoRenew: false` hardcoded.
 * Do not add a toggle here without that open question being resolved first.
 */
export function AdBoostPurchaseCard() {
  const { data: status, isLoading: statusLoading } = useAdBoostStatus();
  const { data: packagesData, isLoading: packagesLoading } = useAdBoostPackages();
  const purchase = usePurchaseAdBoost();

  const packages = (packagesData?.packages ?? []).filter((pkg) => pkg.isActive);
  const sym = currencySymbol(packagesData?.currency);

  const defaultPkg = packages.find((pkg) => pkg.durationDays === 7) ?? packages[0];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = packages.find((pkg) => pkg.packageId === selectedId) ?? defaultPkg;

  const priceOf = (pkg: AdBoostPackageResponse) => pkg.priceMinor / 100;
  const fmt = (n: number) => `${sym}${Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2)}`;

  if (statusLoading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="h-4 w-32 rounded bg-slate-100 animate-pulse mb-4" />
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const activeBoost = status?.adBoost?.status === "ACTIVE" ? status.adBoost : null;

  if (activeBoost) {
    const startsAt = new Date(activeBoost.startsAt);
    const expiresAt = new Date(activeBoost.expiresAt);
    const expired = isPast(expiresAt);

    return (
      <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/60 p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white shrink-0">
            <Rocket className="w-4 h-4" />
          </span>
          <div>
            <p className="text-sm font-bold text-slate-900">Ad Boost active</p>
            <p className="text-xs text-slate-500">Your listing is getting priority placement</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 rounded-xl overflow-hidden">
          <div className="bg-white px-4 py-3">
            <p className="text-xs text-slate-400 font-medium mb-1">Boost score</p>
            <p className="text-xl font-black text-indigo-600 leading-none">
              +{activeBoost.boostScoreApplied}
            </p>
          </div>
          <div className="bg-white px-4 py-3">
            <p className="text-xs text-slate-400 font-medium mb-1">Started</p>
            <p className="text-sm font-semibold text-slate-800 leading-none mt-1">
              {format(startsAt, "MMM d, yyyy")}
            </p>
          </div>
          <div className="bg-white px-4 py-3">
            <p className="text-xs text-slate-400 font-medium mb-1 flex items-center gap-1">
              <CalendarClock className="w-3 h-3" /> {expired ? "Expired" : "Expires"}
            </p>
            <p className="text-sm font-semibold text-slate-800 leading-none mt-1">
              {format(expiresAt, "MMM d, yyyy")}
            </p>
            {!expired && (
              <p className="text-xs text-indigo-500 font-medium mt-1">
                {formatDistanceToNowStrict(expiresAt, { addSuffix: false })} left
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // The rendering rule this component's doc comment has always described, but never implemented.
  // `eligible` is server-authoritative (TeacherAdBoostService: `!hasBenefitBearingSubscription`) and
  // mirrors the exact condition POST /ad-boost/order enforces -- a teacher on a paid tier already
  // gets that tier's boost and cannot buy a second one. Without this branch the picker rendered for
  // everyone, so a subscribed teacher could pick a package, press Buy, and get a bare
  // "Failed to create ad-boost order: 403" from a request the server was always going to refuse.
  //
  // Deliberately placed AFTER the activeBoost branch: a boost bought before the teacher subscribed
  // keeps playing out, and the server keeps reporting it, so it still takes priority.
  if (status && !status.eligible) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-500 shrink-0">
            <Rocket className="w-4 h-4" />
          </span>
          <p className="text-sm font-bold text-slate-900">Ad Boost</p>
        </div>
        <p className="text-xs text-slate-500">
          Your subscription already includes priority placement in search results, so there's no
          need to buy an ad boost. It becomes available again if your subscription ends.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
      <div className="px-4 sm:px-5 pt-4 pb-1">
        <p className="text-sm font-bold text-slate-900">Boost your offerings</p>
        <p className="text-xs text-slate-500 mt-0.5">
          Choose how long you want priority placement in search results.
        </p>
      </div>

      <div className="px-4 sm:px-5 pt-3 pb-4">
        {packagesLoading ? (
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-28 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {packages.map((pkg) => {
              const isSelected = pkg.packageId === (selected?.packageId ?? null);
              return (
                <button
                  key={pkg.packageId}
                  type="button"
                  onClick={() => setSelectedId(pkg.packageId)}
                  className={cn(
                    "relative text-left rounded-xl p-3 flex flex-col gap-1 transition-all duration-200 cursor-pointer border-2",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                    isSelected
                      ? "bg-indigo-50 border-indigo-500 ring-2 ring-indigo-500/20 shadow-sm"
                      : "bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm"
                  )}
                >
                  <p className="text-sm font-black text-slate-900">{DURATION_LABEL[pkg.durationDays]}</p>
                  <p className="text-xs font-semibold text-indigo-600 flex items-center gap-1">
                    <Zap className="w-3 h-3 shrink-0" />+{pkg.boostScore} score
                  </p>
                  <p className="text-sm font-extrabold text-slate-800 mt-1">{fmt(priceOf(pkg))}</p>
                  {isSelected && (
                    <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-indigo-500" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {selected && (
          <button
            type="button"
            onClick={() => purchase.mutate({ packageId: selected.packageId, autoRenew: false })}
            disabled={purchase.isPending}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white text-sm font-bold px-6 py-3 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            {purchase.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Processing…
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4" /> Buy Ad Boost · {fmt(priceOf(selected))}
              </>
            )}
          </button>
        )}

        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-3">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Secured by Razorpay
        </p>
      </div>
    </div>
  );
}

export default AdBoostPurchaseCard;
